import {getAnonymousId} from "@/lib/analytics/anonymous-id";

/**
 * The `anonymous_id` cookie now lives in `anonymous-id.ts` — it is shared with
 * Mixpanel (AC-711) and is no longer Customer.io-specific. Re-exported here so
 * existing `from "@/lib/analytics/customerio"` imports keep working.
 */
export {getAnonymousId};

/**
 * Customer.io (Journeys) client-side tracking — AC-613.
 *
 * We load the public cio.js snippet (EU region) using only the workspace
 * Site ID (`NEXT_PUBLIC_CIO_SITE_ID`). The Track API key is a secret and must
 * NEVER reach the browser — server-side events and the anonymous→player merge
 * are the backend's job. The frontend only emits client-side / pre-registration
 * events directly into the same Journeys workspace.
 *
 * Identity model (Confluence "Retention" §2, §12):
 *  - Unauthenticated visitors are NOT identified. cio.js tracks their events
 *    under an auto-managed anonymous id (`_cioanonid`) and merges that activity
 *    into the player on identify — this is the client-side merge (the backend
 *    no longer does a server-side merge). It requires "Anonymous event merging"
 *    enabled in the workspace and only merges events from the last 30 days.
 *  - Authenticated users are identified by their `player_id` (JWT `sub`) via
 *    `cioIdentifyUser`, which triggers the merge above.
 *
 * We additionally keep our own `anonymous_id` cookie: it is forwarded in the
 * registration request (stored as a person attribute) and attached to §11
 * events as a property, per spec. It is NOT the merge key — cio.js uses its
 * own `_cioanonid` for that.
 */

type CioMethod = (...args: unknown[]) => void;

interface CioApi {
    identify: CioMethod;
    track: CioMethod;
    page: CioMethod;

    [key: string]: unknown;
}

declare global {
    interface Window {
        _cio?: CioApi;
    }
}

export const CIO_SITE_ID = process.env.NEXT_PUBLIC_CIO_SITE_ID;

/**
 * Account region — picks the correct cio.js asset/endpoints. Must match where
 * the Customer.io account physically lives (US: fly.customer.io, EU:
 * fly-eu.customer.io). Set per-environment via `NEXT_PUBLIC_CIO_REGION`
 * (dev is US/222884; prod may differ). Defaults to US.
 */
export const CIO_REGION = (process.env.NEXT_PUBLIC_CIO_REGION || "us").toLowerCase();

/** Region-specific cio.js Journeys snippet asset. */
export const CIO_TRACK_ASSET =
    CIO_REGION === "eu"
        ? "https://assets.customer.io/assets/track-eu.js"
        : "https://assets.customer.io/assets/track.js";

/** The cio.js snippet is only injected when a Site ID is configured. */
export const isCioEnabled = (): boolean => !!CIO_SITE_ID;

/**
 * Enables Customer.io in-app messages (modals/banners/tooltips rendered by
 * cio.js on this site). On by default; set `NEXT_PUBLIC_CIO_IN_APP=false` to
 * opt an environment out. Requires the workspace to be provisioned for in-app
 * (Journeys → Settings → In-App) — on an unprovisioned workspace the renderer
 * simply never receives messages. Delivered to identified users only.
 */
export const CIO_IN_APP_ENABLED = process.env.NEXT_PUBLIC_CIO_IN_APP !== "false";

const CIO_METHODS = ["load", "identify", "sidebar", "track", "page", "on", "off"] as const;

/**
 * Returns the cio.js API, pre-seeding the same queuing array stub the snippet
 * creates. This guarantees early identify/track calls (effects that run before
 * the afterInteractive snippet executes) are queued and replayed once
 * track-eu.js loads, instead of being silently dropped. No-ops when disabled.
 */
const getCio = (): CioApi | undefined => {
    if (typeof window === "undefined" || !isCioEnabled()) return undefined;
    if (!window._cio) {
        const queue: unknown[] = [];
        const stub = queue as unknown as CioApi;
        CIO_METHODS.forEach((method) => {
            stub[method] = (...args: unknown[]) => {
                queue.push([method, ...args]);
            };
        });
        window._cio = stub;
    }
    return window._cio;
};

/**
 * Identify the authenticated player. cio.js auto-merges the anonymous activity
 * it tracked under `_cioanonid` into this profile (client-side merge).
 */
export const cioIdentifyUser = (
    playerId: string,
    traits: Record<string, unknown> = {},
): void => {
    getCio()?.identify({id: playerId, ...traits});
};

/**
 * Low-level track. Prefer `useCio().track` from React components — it injects
 * the correct identity (`player_id` vs `anonymous_id`) automatically.
 */
export const cioTrack = (
    event: string,
    data: Record<string, unknown> = {},
): void => {
    getCio()?.track(event, data);
};

export const cioPage = (
    name?: string,
    data: Record<string, unknown> = {},
): void => {
    getCio()?.page(name, data);
};
